import React, { Fragment, memo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { promotionApi } from '@app/store';
import { ScheduleAvailabilityTypeEnum, ISchedule, OrderMode, PromotionDTO, PromotionScope } from '@app/common';
import { Button, Form, Panel } from '@app/components';
import { PromotionForm } from './PromotionForm';
import { PromotionFormValues, transformStepFormValuesToPayloadPromotion } from './promotion-form.helpers';
import { useToast, useTriptych } from '@app/hooks';
import { InputFileRef } from '@app/components/form/InputFile';
import { PromotionCard } from './PromotionCard';
import { useAppSelector } from '@app/store/hooks';
import { triptychSelectors } from '@app/store/slices/triptych.slice';
import type { ValeursDeCopie } from '../Duplication/cles-privees';

type Props = { copie?: ValeursDeCopie<PromotionFormValues> };

// Le formulaire porte le useForm : il est remonté (clé) quand on passe d'une copie
// au vierge ou d'une copie à une autre, car react-hook-form ne relit
// `defaultValues` qu'au montage.
const PromotionCreateForm = memo(({ copie }: Props) => {
  const toast = useToast();

  const triptych = useTriptych()

  const cardImageRef = useRef<InputFileRef>();

  const [create, { isLoading, error: apiErrors }] = promotionApi.useCreateOnePromotionsMutation();

  const valeursVierges: PromotionFormValues = {
    name: '',
    priority: 1,
    image: null,
    description: '',
    code: undefined,
    conditions: [],
    actions: [],
    scopes: [PromotionScope.CATALOG],
    maxUses: undefined,
    shops: [],
    orderModes: Object.values(OrderMode),
    disabled: false,
    cumulative: false,
    repeatable: false,
    uniqueByCustomer: false,
    availabilityType: ScheduleAvailabilityTypeEnum.always,
    availabilitySchedule: {} as ISchedule,
    connectors: [],
    isPromotionCode: false,
  };

  const defaultValues = copie?.copieDe ? copie : valeursVierges;

  const formMethods = useForm<PromotionFormValues>({
    resolver: classValidatorResolver(PromotionDTO),
    defaultValues,
  });

  const onCreate = async (formValues: PromotionFormValues) => {
    try {
      const promotion = await create(transformStepFormValuesToPayloadPromotion(formValues)).unwrap();

      triptych.close({ mode: 'UPDATE', data: promotion });

      toast.success('Votre promotion a bien été ajoutée.')
    } catch (error) {
      console.error(error);
      toast.error('Une erreur est survenue lors de la création de votre promotion.')
    }
  }

  return (
    <Fragment>
      <PromotionCard
        // l'aperçu de l'original reste affiché tant qu'aucun fichier n'est choisi
        promotion={copie?.copieDe ? { images: copie.imagesDeLOriginal } : undefined}
        formMethods={formMethods}
        toggle={() => triptych.toggle()}
        onOpenImagePicker={() => cardImageRef.current?.open()} />

      <Panel.Content>

        <Form formMethods={formMethods} apiErrors={apiErrors}>

          <Panel.FormContainer>

            <PromotionForm ref={cardImageRef} />

          </Panel.FormContainer>

          <Panel.Actions>
            <Button
              title="Retour"
              grow
              large
              bordered
              onPress={() => triptych.close()} />

            <Button
              loading={isLoading}
              title="Valider"
              onPress={formMethods.handleSubmit(onCreate)}
              grow
              large />
          </Panel.Actions>

        </Form>

      </Panel.Content>
    </Fragment>
  );
});

export const PromotionCreate = memo(() => {
  // Dupliquer : la fiche ouvre ce panneau avec les valeurs de copie dans le triptyque.
  const copie = useAppSelector(triptychSelectors.selectData) as ValeursDeCopie<PromotionFormValues> | undefined;

  return <PromotionCreateForm key={copie?.copieDe ?? 'creation'} copie={copie?.copieDe ? copie : undefined} />;
});
